if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        document.body.classList.remove('overflow-hidden-popup');

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
            window.requestAnimationFrame(() => this.setupQuickAddGallery());
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        const gallery = productElement.querySelector('[id^="Slider-Gallery"]');
         if (!gallery) return;

          gallery.classList.add('quickadd-gallery');
          gallery.classList.add('slider--desktop');
          document.body.classList.add('overflow-hidden-popup');

          gallery.querySelectorAll('.product__media-item').forEach(item => {
            item.classList.add('slider__slide');
          });
      }

      setupQuickAddGallery() {
        const gallery = this.modalContent.querySelector('.quickadd-gallery');
        if (!gallery) return;

        this.resetQuickAddGallerySlider();
        this.setupQuickAddGalleryButtons(gallery);
        this.setupQuickAddGalleryDrag(gallery);
      }

      resetQuickAddGallerySlider() {
        const galleryViewer = this.modalContent.querySelector('slider-component[id^="GalleryViewer"]');
        galleryViewer?.resetPages?.();
      }

      setupQuickAddGalleryButtons(gallery) {
        const sliderComponent = gallery.closest('slider-component');
        if (!sliderComponent || sliderComponent.dataset.quickaddButtonsReady === 'true') return;

        sliderComponent.dataset.quickaddButtonsReady = 'true';
        sliderComponent.querySelectorAll('button[name="previous"], button[name="next"]').forEach((button) => {
          button.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              event.stopImmediatePropagation();

              const direction = button.name === 'next' ? 1 : -1;
              gallery.scrollBy({
                left: this.getQuickAddSlideWidth(gallery) * direction,
                behavior: 'smooth',
              });
            },
            true
          );
        });
      }

      setupQuickAddGalleryDrag(gallery) {
        if (gallery.dataset.quickaddDragReady === 'true') return;

        gallery.dataset.quickaddDragReady = 'true';
        let isDown = false;
        let startX = 0;
        let startScrollLeft = 0;

        gallery.addEventListener('mousedown', (event) => {
          isDown = true;
          startX = event.pageX;
          startScrollLeft = gallery.scrollLeft;
          gallery.classList.add('dragging');
        });

        window.addEventListener('mouseup', (event) => {
          if (isDown) this.snapQuickAddGallery(gallery, startScrollLeft, event.pageX - startX);
          isDown = false;
          gallery.classList.remove('dragging');
        });

        gallery.addEventListener('mouseleave', () => {
          if (isDown) this.snapQuickAddGallery(gallery, startScrollLeft, startScrollLeft - gallery.scrollLeft);
          isDown = false;
          gallery.classList.remove('dragging');
        });

        gallery.addEventListener('mousemove', (event) => {
          if (!isDown) return;
          event.preventDefault();
          gallery.scrollLeft = startScrollLeft - (event.pageX - startX);
        });
      }

      getQuickAddSlideWidth(gallery) {
        const slides = gallery.querySelectorAll('.product__media-item');
        if (slides.length > 1) return slides[1].offsetLeft - slides[0].offsetLeft;

        return slides[0]?.offsetWidth || gallery.clientWidth;
      }

      snapQuickAddGallery(gallery, startScrollLeft = gallery.scrollLeft, dragDistance = 0) {
        const slideWidth = this.getQuickAddSlideWidth(gallery);
        if (!slideWidth) return;

        const slides = gallery.querySelectorAll('.product__media-item');
        const maxIndex = Math.max(slides.length - 1, 0);
        const currentIndex = Math.round(startScrollLeft / slideWidth);
        const scrollDelta = gallery.scrollLeft - startScrollLeft;
        const direction = Math.sign(scrollDelta || -dragDistance);
        const targetIndex = Math.min(Math.max(currentIndex + (Math.abs(scrollDelta) > 6 ? direction : 0), 0), maxIndex);

        gallery.scrollTo({
          left: targetIndex * slideWidth,
          behavior: 'smooth',
        });
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
